import React, { useState } from 'react';
import { ArrowLeft, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTradingConfig, useTestProvider } from '../hooks/useTradingAnalysis';

const TradingProviderTest: React.FC = () => {
  const navigate = useNavigate();
  const { data: config, isLoading: configLoading } = useTradingConfig();
  const testProvider = useTestProvider();
  const [prompt, setPrompt] = useState('Say hello in one sentence.');

  const handleTest = () => {
    if (!prompt.trim()) return;
    testProvider.mutate(prompt.trim());
  };

  if (configLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  const result = testProvider.data;

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/trading/settings')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Provider Connectivity Test</h1>
          <p className="text-slate-400 text-sm">Send a test prompt to verify the active LLM provider</p>
        </div>
      </div>

      {/* Current Config */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-300">Active Configuration</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-slate-700/50 rounded-lg px-3 py-2">
            <span className="text-xs text-slate-400 block">Provider</span>
            <span className="text-sm text-white font-medium">{config?.trading_analyst_provider || 'unknown'}</span>
          </div>
          <div className="bg-slate-700/50 rounded-lg px-3 py-2">
            <span className="text-xs text-slate-400 block">Model</span>
            <span className="text-sm text-white font-medium truncate block">{config?.trading_analyst_model || 'unknown'}</span>
          </div>
          <div className="bg-slate-700/50 rounded-lg px-3 py-2">
            <span className="text-xs text-slate-400 block">Copilot SDK</span>
            <span className={`text-sm font-medium ${config?.copilot_available ? 'text-green-400' : 'text-slate-500'}`}>
              {config?.copilot_available ? 'Available' : 'Not installed'}
            </span>
          </div>
        </div>
      </div>

      {/* Prompt Input */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-300">Test Prompt</h3>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500 resize-none"
          placeholder="Enter a test prompt..."
        />
        <button
          onClick={handleTest}
          disabled={testProvider.isPending || !prompt.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          {testProvider.isPending ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <Send size={16} />
              Send Test
            </>
          )}
        </button>
      </div>

      {/* Result */}
      {testProvider.isError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 space-y-2">
          <div className="flex items-center gap-2">
            <XCircle size={18} className="text-red-400" />
            <span className="text-sm font-semibold text-red-400">Request Failed</span>
          </div>
          <p className="text-sm text-red-300">{(testProvider.error as Error).message}</p>
        </div>
      )}

      {result && (
        <div className={`rounded-xl border p-5 space-y-4 ${
          result.success
            ? 'bg-slate-800/50 border-slate-700/50'
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {result.success ? (
                <CheckCircle size={18} className="text-green-400" />
              ) : (
                <XCircle size={18} className="text-red-400" />
              )}
              <span className={`text-sm font-semibold ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                {result.success ? 'Success' : 'Failed'}
              </span>
            </div>
            {result.latency_ms > 0 && (
              <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded">
                {result.latency_ms} ms
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-slate-400 block">Provider</span>
              <span className="text-sm text-white">{result.provider}</span>
            </div>
            <div>
              <span className="text-xs text-slate-400 block">Model</span>
              <span className="text-sm text-white truncate block">{result.model}</span>
            </div>
          </div>

          {result.error && (
            <div>
              <span className="text-xs text-slate-400 block mb-1">Error</span>
              <p className="text-sm text-red-300 bg-red-500/10 rounded-lg px-3 py-2">{result.error}</p>
            </div>
          )}

          {result.response && (
            <div>
              <span className="text-xs text-slate-400 block mb-1">Response</span>
              <p className="text-sm text-slate-200 bg-slate-700/50 rounded-lg px-3 py-2 whitespace-pre-wrap">
                {result.response}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TradingProviderTest;
